import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { environment } from '@env/environment';
import { Observable } from 'rxjs';

export interface ClientRegistrationResponse {
  client: {
    id: number;
    active: boolean;
    name: string;
    domain: string;
    industry: string;
  };
  branding: {
    clientId: number;
    colorPalette: string | null;
    colorPaletteAlt: string | null;
    icon: string | null;
    logo: string | null;
  };
  member: {
    id: number;
    clientId: number;
    roleId: number;
    firstName: string;
    lastName: string;
  };
  provisioningStatus: 'pending' | 'in_progress' | 'completed' | 'failed';
  provisioningMessage?: string;
}

export interface ProvisioningStatus {
  clientId: number;
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'not_found';
  error?: string;
  completedAt?: string;
  portalUrl?: string;
  /** True when CloudFront and S3 are saved on the client; show "Your portal is ready" only when this is true. */
  portalReady?: boolean;
  /** True when CloudFront and S3 are saved on the client. */
  storageReady?: boolean;
  /** True when the client is active and has a connected Stripe account. */
  activationReady?: boolean;
  /** True when provisioning completed successfully and the welcome email was sent. */
  welcomeEmailSent?: boolean;
  /** Short, user-facing label for the current provisioning step. */
  currentStepTitle?: string;
  /** Short, user-facing description for the current provisioning step. */
  currentStepDescription?: string;
}

export interface SignupVerificationStartRequest {
  email: string;
  organizationName: string;
  firstName: string;
  lastName: string;
  /** Cloudflare Turnstile token; the API verifies it before emailing a code. */
  turnstileToken: string;
}

export interface SignupVerificationStartResponse {
  verificationId: string;
  expiresInMinutes: number;
  email: string;
}

/** Proof the admin email was verified; required by POST /public/client/register before anything is provisioned. */
export interface SignupEmailVerification {
  verificationId: string;
  code: string;
}

@Injectable({
  providedIn: 'root'
})
export class ClientOnboardingService {

  constructor(private readonly httpClient: HttpClient) { }

  /** Step 1: bot check + email a 6-digit code to the admin. Nothing is created yet. */
  startEmailVerification(request: SignupVerificationStartRequest): Observable<SignupVerificationStartResponse> {
    return this.httpClient.post<SignupVerificationStartResponse>(`${environment.apiUrl}/public/client/register/start`, request);
  }

  /** Optional early check of the emailed code so the form can move on; provisions nothing. */
  verifyEmailCode(email: string, verification: SignupEmailVerification): Observable<{ verified: boolean }> {
    return this.httpClient.post<{ verified: boolean }>(`${environment.apiUrl}/public/client/register/verify`, {
      email,
      verificationId: verification.verificationId,
      code: verification.code
    });
  }

  registerClient(
    clientForm: FormGroup,
    paymentMethodId: string | null = null,
    verification: SignupEmailVerification | null = null
  ): Observable<ClientRegistrationResponse> {
    const adminInfo = clientForm.value.adminInformation;
    const { password, ...memberWithoutPassword } = adminInfo;
    const platformPackage = clientForm.value.packageInformation?.platformPackage === 'community'
      ? 'society'
      : clientForm.value.packageInformation?.platformPackage || 'society';
    
    const transformedData = {
      client: {
        ...clientForm.value.organizationInformation,
        administrativeEmail: adminInfo.email,  // Set to member email
        billingEmail: adminInfo.email  // Also set billing email
      },
      member: memberWithoutPassword,
      password: password,
      branding: clientForm.value.brandingInformation || null,
      platformPackage,
      paymentMethodId: paymentMethodId || null,  // Stripe payment method for platform billing
      emailVerificationId: verification?.verificationId ?? null,
      emailVerificationCode: verification?.code ?? null
    };
    return this.httpClient.post<ClientRegistrationResponse>(environment.apiUrl + '/public/client/register', transformedData);
  }

  getProvisioningStatus(clientId: number): Observable<ProvisioningStatus> {
    return this.httpClient.get<ProvisioningStatus>(`${environment.apiUrl}/public/client/provisioning-status/${clientId}`);
  }

  editBranding(brandingForm: FormGroup, clientId: number) {
    const formData = new FormData();
    

    formData.append('logo', brandingForm.get('logo')?.value || null);
    formData.append('icon', brandingForm.get('icon')?.value || null);
    
    const brandingData = {
      colorPalette: brandingForm.get('colorPalette')?.value || '',
      colorPaletteAlt: brandingForm.get('colorPaletteAlt')?.value || '',
      clientId: clientId
    };

    formData.append('branding', JSON.stringify(brandingData));

    return this.httpClient.put(`${environment.apiUrl}/client/branding/edit`, formData, {withCredentials: true, headers: { 'enctype': 'multipart/form-data' }});

  }
}
